'use client';

import { useCallback, useRef, useEffect, useTransition } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { startNavigationProgress } from '../providers/nprogress';

interface NavigationOptions {
  replace?: boolean;
  scroll?: boolean;
  immediate?: boolean; // Add flag for immediate navigation without transition
}

interface AppNavigationReturn {
  navigate: (path: string, options?: NavigationOptions) => void;
  isNavigating: boolean;
  currentPath: string;
  back: () => void;
  refresh: () => void;
}

/**
 * Unified navigation hook for the entire app.
 * 
 * Key improvements:
 * - Single source of truth for navigation state
 * - Selective use of transitions (only for heavy navigations)
 * - Prevents duplicate navigations automatically
 * - Handles edge cases cleanly
 */
export function useAppNavigation(): AppNavigationReturn {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  
  // Track pending navigation to prevent duplicates
  const pendingNavigation = useRef<string | null>(null);

  // Normalize a path by sorting query params and removing trailing slashes (except root)
  const normalizePath = useCallback((rawPath: string): string => {
    try {
      // Ensure absolute URL for URL constructor
      const base = typeof window !== 'undefined' ? window.location.origin : 'https://voley.local';
      const url = new URL(rawPath, base);
      // Sort search params for stable comparisons
      const entries = Array.from(url.searchParams.entries()).sort(([a], [b]) => a.localeCompare(b));
      url.search = '';
      for (const [k, v] of entries) {
        url.searchParams.append(k, v);
      }
      // Remove trailing slash except for root
      let normalizedPathname = url.pathname;
      if (normalizedPathname.length > 1 && normalizedPathname.endsWith('/')) {
        normalizedPathname = normalizedPathname.replace(/\/+$/, '');
      }
      return normalizedPathname + (url.search ? `?${url.searchParams.toString()}` : '');
    } catch {
      // Fallback to raw path if URL parsing fails
      return rawPath;
    }
  }, []);
  
  // Clear pending navigation when path changes
  useEffect(() => {
    pendingNavigation.current = null;
  }, [pathname]);
  
  const navigationInProgress = isPending;
  
  const navigate = useCallback((path: string, options?: NavigationOptions) => {
    const normalizedTarget = normalizePath(path);
    const normalizedCurrent = typeof window !== 'undefined'
      ? normalizePath(`${window.location.pathname}${window.location.search}`)
      : normalizePath(pathname);

    // Skip if already navigating to the same normalized path
    if (normalizedTarget === normalizedCurrent || normalizedTarget === pendingNavigation.current) {
      return;
    }
    
    // Mark as pending
    pendingNavigation.current = normalizedTarget;

    // Extract just the pathname (without query params) to check if it's a same-page navigation
    const targetPathname = normalizedTarget.split('?')[0];
    const currentPathname = normalizedCurrent.split('?')[0];
    const isSamePageNavigation = targetPathname === currentPathname;

    // Only start progress bar for actual page changes, not query param changes
    // Query param changes (like selecting a conversation) are instant and don't need progress
    if (!isSamePageNavigation) {
      startNavigationProgress();
    }

    const scrollOption = options?.scroll ?? true;

    // For conversation switches and immediate navigation, skip transition
    const isConversationSwitch = normalizedTarget.includes('conversation=') && pathname.includes('/dashboard');
    const shouldUseTransition = !options?.immediate && !isConversationSwitch;
    
    const doNavigation = () => {
      if (options?.replace) {
        router.replace(normalizedTarget, { scroll: scrollOption });
      } else {
        router.push(normalizedTarget, { scroll: scrollOption });
      }
    };
    
    if (shouldUseTransition) {
      startTransition(() => {
        doNavigation();
      });
    } else {
      doNavigation();
    }
  }, [pathname, router, normalizePath]);
  
  const back = useCallback(() => {
    startTransition(() => {
      router.back();
    });
  }, [router]);
  
  const refresh = useCallback(() => {
    startTransition(() => {
      router.refresh();
    });
  }, [router]);
  
  return {
    navigate,
    isNavigating: navigationInProgress,
    currentPath: pathname,
    back,
    refresh,
  };
}
